import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { CURRENT_WHATS_NEW_VERSION } from '../utils/whatsNewVersion';

const ITEM_KEYS = [
  'race_engineer_back',
  'billing_fix',
  'tires_module',
  'barcode_scanner',
  'post_session_wear',
  'manual_updated',
];

export default function WhatsNewDialog({ userId }) {
  const { t, i18n } = useTranslation('main-layout');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Al primo mount: legge whatsNewSeen sul doc utente
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'users', userId);
        const snap = await getDoc(ref);
        const seen = snap.exists() ? snap.data()?.whatsNewSeen : null;
        if (!cancelled && seen !== CURRENT_WHATS_NEW_VERSION) {
          setOpen(true);
        }
      } catch (_) {
        // In caso di errore di lettura non blocchiamo l'app: niente popup.
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const markSeen = async () => {
    if (!userId) { setOpen(false); return; }
    setSaving(true);
    try {
      const ref = doc(db, 'users', userId);
      await updateDoc(ref, { whatsNewSeen: CURRENT_WHATS_NEW_VERSION });
    } catch (_) {
      // se updateDoc fallisce (doc inesistente), prova setDoc merge
      try {
        await setDoc(doc(db, 'users', userId), { whatsNewSeen: CURRENT_WHATS_NEW_VERSION }, { merge: true });
      } catch (__) { /* swallow: il popup rimane comunque chiuso lato UI */ }
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  const openManual = () => {
    const lang = (i18n.language || 'it').toLowerCase().startsWith('en') ? 'en' : 'it';
    const url = lang === 'en' ? '/docs/user-manual-en.html#s6-4' : '/docs/manuale-utente.html#s6-4';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={markSeen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <MenuBookIcon color="primary" />
        {t('whats_new.title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('whats_new.subtitle')}
        </Typography>
        <List dense sx={{ py: 0 }}>
          {ITEM_KEYS.map(key => (
            <ListItem key={key} alignItems="flex-start" sx={{ py: 0.5, pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    component="span"
                    dangerouslySetInnerHTML={{ __html: t(`whats_new.items.${key}`) }}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('whats_new.footer_note')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={openManual} startIcon={<MenuBookIcon />}>
          {t('whats_new.manual_cta')}
        </Button>
        <Button variant="contained" onClick={markSeen} disabled={saving}>
          {t('whats_new.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
